import { AsYouType, parsePhoneNumberFromString, type PhoneNumber } from 'libphonenumber-js/max'

const DEFAULT_COUNTRY = 'BG'
const MOBILE_TYPES = new Set(['MOBILE', 'FIXED_LINE_OR_MOBILE'])
const BG_PHONE_TYPES = new Set(['MOBILE', 'FIXED_LINE_OR_MOBILE', 'FIXED_LINE'])

export function sanitizeBgPhoneInput(raw: string): string {
  let value = raw.replace(/[^\d+\s()-]/g, '')
  const firstPlus = value.indexOf('+')
  if (firstPlus !== -1) {
    value = `+${value.slice(firstPlus + 1).replace(/\+/g, '')}`
  }
  return value
}

/** Formats while typing and strips invalid characters. */
export function normalizeBgPhoneInput(raw: string): string {
  return new AsYouType(DEFAULT_COUNTRY).input(sanitizeBgPhoneInput(raw))
}

/** Collapses to digits for reliable parsing (0888… / +359…). */
export function normalizeBgPhoneDigits(raw: string): string {
  const sanitized = sanitizeBgPhoneInput(raw.trim())
  if (!sanitized) return ''
  const hasPlus = sanitized.startsWith('+')
  const digits = sanitized.replace(/\D/g, '')
  if (!digits) return hasPlus ? '+' : ''
  return hasPlus ? `+${digits}` : digits
}

export function parseBgPhone(raw: string): PhoneNumber | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined

  for (const candidate of [trimmed, normalizeBgPhoneDigits(trimmed)]) {
    if (!candidate) continue
    const parsed = parsePhoneNumberFromString(candidate, DEFAULT_COUNTRY)
    if (parsed) return parsed
  }
  return undefined
}

export function isBgMobilePhone(parsed: PhoneNumber): boolean {
  const type = parsed.getType()
  return parsed.country === DEFAULT_COUNTRY
    && parsed.isValid()
    && !!type
    && MOBILE_TYPES.has(type)
}

export function isBgPhone(parsed: PhoneNumber): boolean {
  const type = parsed.getType()
  return parsed.country === DEFAULT_COUNTRY
    && parsed.isValid()
    && !!type
    && BG_PHONE_TYPES.has(type)
}

export function formatBgPhone(parsed: PhoneNumber): string {
  if (parsed.getType() === 'MOBILE') {
    const groups = parsed.nationalNumber.match(/.{1,3}/g) ?? []
    return `+${parsed.countryCallingCode} ${groups.join(' ')}`
  }
  return parsed.formatInternational()
}

/** Account phone for the profile and express order. Stored values are digits (`359…`). */
export function displayAccountPhone(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  const digits = trimmed.replace(/\D/g, '')
  const candidate = digits.startsWith('359') ? `+${digits}` : trimmed
  const parsed = parseBgPhone(candidate)
  if (!parsed || !isBgPhone(parsed)) return trimmed
  return formatBgPhone(parsed)
}

export type BgMobilePhoneValidation = {
  valid: boolean
  formatted: string
  parsed?: PhoneNumber
}

export type BgPhoneValidation = BgMobilePhoneValidation

export function validateBgMobilePhone(raw: string): BgMobilePhoneValidation {
  const trimmed = raw.trim()
  if (!trimmed) {
    return { valid: false, formatted: '' }
  }

  const parsed = parseBgPhone(trimmed)
  if (!parsed || !isBgMobilePhone(parsed)) {
    return { valid: false, formatted: trimmed }
  }
  return { valid: true, formatted: formatBgPhone(parsed), parsed }
}

export function validateBgPhone(raw: string): BgPhoneValidation {
  const trimmed = raw.trim()
  if (!trimmed) {
    return { valid: false, formatted: '' }
  }

  const parsed = parseBgPhone(trimmed)
  if (!parsed || !isBgPhone(parsed)) {
    return { valid: false, formatted: trimmed }
  }
  return { valid: true, formatted: formatBgPhone(parsed), parsed }
}
